/* eslint-disable typescript-extended-rules/file-naming-convention */
import {
	BeneficiaryReconciliationMappingBenefitKind,
	BeneficiaryReconciliationMappingCanonicalField,
	BeneficiaryReconciliationMappingFieldState,
	BeneficiaryReconciliationMappingTitularityKind,
} from '../../../../../modules/beneficiary-reconciliations/models/beneficiary-reconciliation.model';

export interface BeneficiaryReconciliationMappingFieldMetadata {
	label: string;
	value: BeneficiaryReconciliationMappingCanonicalField;
}

export const BENEFICIARY_RECONCILIATION_MAPPING_FIELD_METADATA: BeneficiaryReconciliationMappingFieldMetadata[] =
	[
		{
			label: 'Nome',
			value: BeneficiaryReconciliationMappingCanonicalField.BENEFICIARY_NAME,
		},
		{
			label: 'CPF',
			value: BeneficiaryReconciliationMappingCanonicalField.BENEFICIARY_CPF,
		},
		{
			label: 'Data de nascimento',
			value: BeneficiaryReconciliationMappingCanonicalField.BIRTH_DAY,
		},
		{
			label: 'CNPJ',
			value:
				BeneficiaryReconciliationMappingCanonicalField.SOURCE_ORGANIZATION_CNPJ,
		},
		{
			label: 'Operadora',
			value: BeneficiaryReconciliationMappingCanonicalField.CARRIER,
		},
		{
			label: 'Titularidade',
			value: BeneficiaryReconciliationMappingCanonicalField.TITULARITY,
		},
		{
			label: 'CPF do titular',
			value: BeneficiaryReconciliationMappingCanonicalField.HOLDER_CPF,
		},
		{
			label: 'Parentesco',
			value: BeneficiaryReconciliationMappingCanonicalField.PARENTESCO,
		},
		{
			label: 'Plano Saúde',
			value: BeneficiaryReconciliationMappingCanonicalField.HEALTH_PLAN_NAME,
		},
		{
			label: 'Carteirinha Saúde',
			value: BeneficiaryReconciliationMappingCanonicalField.HEALTH_CARD_NUMBER,
		},
		{
			label: 'Plano Dental',
			value: BeneficiaryReconciliationMappingCanonicalField.DENTAL_PLAN_NAME,
		},
		{
			label: 'Carteirinha Dental',
			value: BeneficiaryReconciliationMappingCanonicalField.DENTAL_CARD_NUMBER,
		},
		{
			label: 'Plano Congênere',
			value: BeneficiaryReconciliationMappingCanonicalField.CONGENER_PLAN_NAME,
		},
		{
			label: 'Carteirinha Congênere',
			value:
				BeneficiaryReconciliationMappingCanonicalField.CONGENER_CARD_NUMBER,
		},
		{
			label: 'Plano Conjugado',
			value:
				BeneficiaryReconciliationMappingCanonicalField.CONJUGATED_PLAN_NAME,
		},
		{
			label: 'Carteirinha Conjugado',
			value:
				BeneficiaryReconciliationMappingCanonicalField.CONJUGATED_CARD_NUMBER,
		},
	];

export const BENEFICIARY_RECONCILIATION_MAPPING_BENEFIT_OPTIONS = [
	{
		label: 'Saúde',
		value: BeneficiaryReconciliationMappingBenefitKind.HEALTH,
	},
	{
		label: 'Dental',
		value: BeneficiaryReconciliationMappingBenefitKind.DENTAL,
	},
	{
		label: 'Saúde/Dental',
		value: BeneficiaryReconciliationMappingBenefitKind.HEALTH_DENTAL,
	},
	{
		label: 'Conjugado',
		value: BeneficiaryReconciliationMappingBenefitKind.CONJUGATED,
	},
] as const;

export const BENEFICIARY_RECONCILIATION_MAPPING_TITULARITY_OPTIONS = [
	{
		label: 'Titulares',
		value: BeneficiaryReconciliationMappingTitularityKind.HOLDERS,
	},
	{
		label: 'Dependentes',
		value: BeneficiaryReconciliationMappingTitularityKind.DEPENDENTS,
	},
	{
		label: 'Titulares/Dependentes',
		value: BeneficiaryReconciliationMappingTitularityKind.HOLDERS_DEPENDENTS,
	},
] as const;

export const BENEFICIARY_RECONCILIATION_MAPPING_GENERAL_REQUIRED_FIELDS = [
	BeneficiaryReconciliationMappingCanonicalField.BENEFICIARY_NAME,
	BeneficiaryReconciliationMappingCanonicalField.BENEFICIARY_CPF,
	BeneficiaryReconciliationMappingCanonicalField.BIRTH_DAY,
	BeneficiaryReconciliationMappingCanonicalField.SOURCE_ORGANIZATION_CNPJ,
	BeneficiaryReconciliationMappingCanonicalField.CARRIER,
] as const;

export const BENEFICIARY_RECONCILIATION_MAPPING_HEALTH_FIELDS = [
	BeneficiaryReconciliationMappingCanonicalField.HEALTH_PLAN_NAME,
	BeneficiaryReconciliationMappingCanonicalField.HEALTH_CARD_NUMBER,
] as const;

export const BENEFICIARY_RECONCILIATION_MAPPING_DENTAL_FIELDS = [
	BeneficiaryReconciliationMappingCanonicalField.DENTAL_PLAN_NAME,
	BeneficiaryReconciliationMappingCanonicalField.DENTAL_CARD_NUMBER,
] as const;

export const BENEFICIARY_RECONCILIATION_MAPPING_CONJUGATED_FIELDS = [
	BeneficiaryReconciliationMappingCanonicalField.CONJUGATED_PLAN_NAME,
	BeneficiaryReconciliationMappingCanonicalField.CONJUGATED_CARD_NUMBER,
] as const;

export const BENEFICIARY_RECONCILIATION_MAPPING_PRINCIPAL_BENEFIT_FIELDS = [
	...BENEFICIARY_RECONCILIATION_MAPPING_HEALTH_FIELDS,
	...BENEFICIARY_RECONCILIATION_MAPPING_DENTAL_FIELDS,
] as const;

export const BENEFICIARY_RECONCILIATION_MAPPING_CONGENER_FIELDS = [
	BeneficiaryReconciliationMappingCanonicalField.CONGENER_PLAN_NAME,
	BeneficiaryReconciliationMappingCanonicalField.CONGENER_CARD_NUMBER,
] as const;

export const BENEFICIARY_RECONCILIATION_MAPPING_VARIANT_FIELDS = [
	...BENEFICIARY_RECONCILIATION_MAPPING_CONGENER_FIELDS,
] as const;

export function mappingFieldAllowsMissingFromSource(
	field: BeneficiaryReconciliationMappingCanonicalField,
	benefitKind?: BeneficiaryReconciliationMappingBenefitKind | null,
): boolean {
	if (
		BENEFICIARY_RECONCILIATION_MAPPING_PRINCIPAL_BENEFIT_FIELDS.includes(
			field as (typeof BENEFICIARY_RECONCILIATION_MAPPING_PRINCIPAL_BENEFIT_FIELDS)[number],
		)
	) {
		return true;
	}

	return (
		benefitKind === BeneficiaryReconciliationMappingBenefitKind.CONJUGATED &&
		BENEFICIARY_RECONCILIATION_MAPPING_CONJUGATED_FIELDS.includes(
			field as (typeof BENEFICIARY_RECONCILIATION_MAPPING_CONJUGATED_FIELDS)[number],
		)
	);
}

export function mappingFieldAllowsNotApplicable(
	field: BeneficiaryReconciliationMappingCanonicalField,
): boolean {
	return BENEFICIARY_RECONCILIATION_MAPPING_VARIANT_FIELDS.includes(
		field as (typeof BENEFICIARY_RECONCILIATION_MAPPING_VARIANT_FIELDS)[number],
	);
}

export function getMappingFieldLabel(
	field: BeneficiaryReconciliationMappingCanonicalField,
): string {
	return (
		BENEFICIARY_RECONCILIATION_MAPPING_FIELD_METADATA.find(
			(item) => item.value === field,
		)?.label ?? field
	);
}

export function getMappingSpecialStateLabel(
	state: BeneficiaryReconciliationMappingFieldState,
): string {
	switch (state) {
		case BeneficiaryReconciliationMappingFieldState.MISSING_FROM_SOURCE:
			return 'Não veio na base';
		case BeneficiaryReconciliationMappingFieldState.NOT_APPLICABLE:
			return 'Não se aplica';
		default:
			return state;
	}
}
